import type {
    ComposableOptionsBase,
    ContentBlockRendered,
    ContentBlockRenderedLinkedOptionsResponse,
    ContentBlocksByPlacementResponseExtended
} from '~/types';
import {
    useCustomFetch
} from '~/composables';
import type { NitroFetchOptions } from 'nitropack';
import { isNil as lo_isNil } from 'es-toolkit';

export interface UseContentBlocksOptions extends ComposableOptionsBase {}

export interface ContentBlocksByPlacementConfig {
    placement?: string;
    type?: string;
    uid?: string;
}
export function useContentBlocks(options: Partial<UseContentBlocksOptions> = {}) {
    const apiBasePath = '/v1/content';
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });
    const route = useRoute();
    const previewTokens = createPreviewTokens();


    function createPreviewTokens(): Map<string, string> {
        const preview = route.query.preview;
        const values = Array.isArray(preview) ? preview : [preview];

        const tokens = new Map<string, string>();

        for (const value of values) {
            if (typeof value !== 'string' || !value) {
                continue;
            }

            const key = value.split(':', 1)[0];

            if (key) {
                tokens.set(key, value);
            }
        }

        return tokens;
    }

    function getPreviewToken(key: string): string | undefined {
        return previewTokens.get(key);
    }


    async function getContentBlock(uid: string, linkedPk?: MaybeRef<Nullable<number>>): Promise<ContentBlockRendered> {
        const url = `${apiBasePath}/blocks/${uid}/`;
        const pkValue = unref(linkedPk);

        const config: NitroFetchOptions<string> = {
            method: 'GET'
        };

        if (!lo_isNil(pkValue)) {
            config.query = { linked_pk: pkValue };
        }

        return await $_fetch<any>(url, config);
    }

    async function getContentBlockLinkedOptions(uid: string): Promise<ContentBlockRenderedLinkedOptionsResponse> {
        const url = `${apiBasePath}/blocks/${uid}/linked-options/`;

        const config: NitroFetchOptions<string> = {
            method: 'GET'
        };

        return await $_fetch<ContentBlockRenderedLinkedOptionsResponse>(url, config);
    }

    async function getContentBlocksByPlacement<TExtra = unknown>(
        { placement, type, uid }: ContentBlocksByPlacementConfig,
        token?: string
    ): Promise<ContentBlocksByPlacementResponseExtended<TExtra>> {
        let url: string;

        if (placement) {
            url = `${apiBasePath}/placements/${placement}/blocks/`;
        } else if (type && uid) {
            url = `${apiBasePath}/${type}/${uid}/`;
        } else {
            throw new Error('getContentBlocksByPlacement requires either `placement` or both `type` and `uid`');
        }

        const config: NitroFetchOptions<string> = {
            method: 'GET'
        };

        if (token) {
            config.query = { preview: token };
        }

        return await $_fetch<ContentBlocksByPlacementResponseExtended<TExtra>>(url, config);
    }

    return {
        getContentBlock,
        getContentBlockLinkedOptions,
        getContentBlocksByPlacement,
        previewTokens,
        getPreviewToken
    };
}
